import './order-details-section.css';

import { Util } from '../../../../../utils/util';
import { LocalizationService } from '../../../../../services/localization-service';
import { MenuItemCartCardController } from '../../../../shared/MenuItemCartCard/menu-item-cart-card-controller';
import { IMenuItemCartCardUiParams } from '../../../../shared/MenuItemCartCard/menu-item-cart-card-base';

const TOGGLE_BUTTON_ID = "btn_checkout_order_details";
const ITEMS_CONTAINER_ID = "div_checkout_order_details_items";

/**
 * Collapsible "Order Details" list shown below the location block on the OrderHub checkout,
 * rendering the order's items in the Order Confirmation card style.
 */
export class OrderDetailsSection {
    private readonly _localizationService: LocalizationService;
    private readonly _menuItems: MenuItemCartCardController;
    private readonly _containerElement: HTMLElement;
    private _isExpanded: boolean = false;

    /**
     * Initializes the OrderDetailsSection component.
     * @param {Omit<IMenuItemCartCardUiParams, "parentDivId">} params - Dependencies for the item cards.
     */
    constructor(params: Omit<IMenuItemCartCardUiParams, "parentDivId">) {
        this._localizationService = params.localizationService;
        this._menuItems = new MenuItemCartCardController({ ...params, parentDivId: ITEMS_CONTAINER_ID });

        this._containerElement = document.createElement("div");
        this._containerElement.classList.add("checkout-order-details");
        this._containerElement.innerHTML = `
            <button type="button" id="${TOGGLE_BUTTON_ID}" class="checkout-order-details-toggle button-link"
                    aria-expanded="false" aria-controls="${ITEMS_CONTAINER_ID}">
                <span ltag="OrderDetails"></span>
                <i class="icon-arrow-back" aria-hidden="true"></i>
            </button>
            <div id="${ITEMS_CONTAINER_ID}" class="checkout-order-details-items hidden" role="region"
                 aria-labelledby="${TOGGLE_BUTTON_ID}" aria-hidden="true"></div>`;

        this._toggleButton.addEventListener("click", this._handleToggle);
    }

    private get _toggleButton(): HTMLButtonElement {
        return this._containerElement.querySelector(`#${TOGGLE_BUTTON_ID}`) as HTMLButtonElement;
    }

    private get _itemsContainer(): HTMLElement {
        return this._containerElement.querySelector(`#${ITEMS_CONTAINER_ID}`) as HTMLElement;
    }

    private readonly _handleToggle = (): void => {
        this._setExpanded(!this._isExpanded);
    }

    /**
     * Expands or collapses the item list, keeping aria-expanded (which drives the chevron) in sync.
     * @param {boolean} expanded - Whether the list should be open.
     * @private
     */
    private _setExpanded(expanded: boolean): void {
        this._isExpanded = expanded;
        this._toggleButton.setAttribute("aria-expanded", String(expanded));

        if (!expanded) {
            // Cards reuse the Confirmation modal's element ids, so drop them instead of leaving hidden duplicates
            this._itemsContainer.innerHTML = "";
            Util.hideElement(this._itemsContainer);
            return;
        }

        // Cards render by element id, so the container must already be attached to the document
        this._menuItems.ui?.init(false, true);
        Util.showElement(this._itemsContainer);
    }

    /**
     * Retrieves the root element of the order details section.
     * @returns {HTMLElement} The container element.
     */
    public get element(): HTMLElement {
        return this._containerElement;
    }

    /**
     * Whether the item list is currently open.
     * @returns {boolean} True when expanded.
     */
    public get isExpanded(): boolean {
        return this._isExpanded;
    }

    /**
     * Collapses the item list and removes its cards from the DOM.
     */
    public collapse(): void {
        this._setExpanded(false);
    }

    /**
     * Renders the label and resets the section to its default collapsed state.
     */
    public init(): void {
        this._toggleButton.querySelector("span")!.textContent = this._localizationService.names("OrderDetails");
        this.collapse();
    }
}
